import React, { useCallback, useRef } from 'react';

const INTERACTIVE_SELECTOR = [
    'a',
    'button',
    'input',
    'textarea',
    'select',
    '[role="button"]',
    '[role="link"]',
    '[role="combobox"]',
    '[contenteditable="true"]',
    '[data-item-single-click]',
].join(',');

/**
 * Returns true when a mouse/keyboard event originated inside an interactive
 * descendant of the given container — meaning the container should NOT
 * activate its implicit item-open behavior.
 *
 * Use this in every shared item container (table row, card, virtual row)
 * before invoking the double-click or Enter callback.
 */
export function isInteractiveTarget(
    event: React.MouseEvent | React.KeyboardEvent,
    container: HTMLElement,
): boolean {
    const target = event.target as HTMLElement;
    if (target === container) return false;
    const interactive = target.closest?.(INTERACTIVE_SELECTOR);
    // A component may intentionally be both the activation container and a
    // semantic button (the Sales Summary month control is one example). Its
    // own label is still part of the activation surface; only a descendant
    // interactive control should be protected from the parent callback.
    return !!interactive && interactive !== container;
}

const DOUBLE_TAP_WINDOW_MS = 400;
const DOUBLE_TAP_DISTANCE_PX = 24;

export interface TouchDoubleTapState {
    time: number;
    x: number;
    y: number;
    target: EventTarget | null;
    itemKey?: React.Key;
}

/**
 * Mirrors the implicit-item double-click rule for touch devices. Browsers do
 * not consistently dispatch `dblclick` for a touch double-tap (and iPadOS may
 * reserve it for zoom/press gestures), so the second tap is detected from the
 * two touchend events. The first tap is deliberately left alone so normal
 * press/click behavior is preserved.
 */
export function handleTouchDoubleTap<T extends HTMLElement>(
    event: React.TouchEvent<T>,
    state: React.MutableRefObject<TouchDoubleTapState | null>,
    onActivate: (() => void) | undefined,
    itemKey?: React.Key,
): void {
    if (!onActivate || event.changedTouches.length !== 1) {
        state.current = null;
        return;
    }

    const container = event.currentTarget;
    if (isInteractiveTarget(event as unknown as React.MouseEvent, container)) {
        state.current = null;
        return;
    }

    const touch = event.changedTouches[0];
    const now = Date.now();
    const previous = state.current;
    const distance = previous
        ? Math.hypot(touch.clientX - previous.x, touch.clientY - previous.y)
        : Infinity;
    const isDoubleTap =
        previous !== null &&
        previous.target === container &&
        previous.itemKey === itemKey &&
        now - previous.time <= DOUBLE_TAP_WINDOW_MS &&
        distance <= DOUBLE_TAP_DISTANCE_PX;

    if (isDoubleTap) {
        state.current = null;
        // Only the activating second tap is consumed; the first one remains a
        // regular press, which is important for touch-friendly controls.
        event.preventDefault();
        onActivate();
        return;
    }

    state.current = {
        time: now,
        x: touch.clientX,
        y: touch.clientY,
        target: container,
        itemKey,
    };
}

/**
 * Hook form used by row/card components that own one stable DOM container.
 */
export function useTouchDoubleActivation(
    onActivate?: () => void,
    itemKey?: React.Key,
): (event: React.TouchEvent<HTMLElement>) => void {
    const state = useRef<TouchDoubleTapState | null>(null);
    return useCallback(
        (event: React.TouchEvent<HTMLElement>) => {
            handleTouchDoubleTap(event, state, onActivate, itemKey);
        },
        [onActivate, itemKey],
    );
}
